import { filterOnlineOfflineData } from "../../../shared_components/graphing/resources/utils"
import { arrayWithContents, convertTZ } from "../../../shared_components/utils"
import { id_accessor } from "../../shared_components_registration"
import { getLocaleFormattedUserString } from "../../internationalization/LocaleFormatted"

function getDataLayers(layers, observationAccessor, name) {
  return layers
    .filter((l) => !l.disabled)
    .map((l) => ({
      name: name,
      ...l,
      ...(observationAccessor(l.tag, l.units, l.smp_src) || { pending: true }),
    }))
}

function getFilteredDataLayers(layers, timeExtractor, graphKey = null) {
  let loading = false
  const filteredLayers = layers
    .filter((layer) => {
      if (layer.data) {
        return true
      } else if (layer.optional) {
        return false
      } else if (layer.loading) {
        loading = true
      }
    })
    .map((layer) => {
      let sortedData = [...layer.data]
      if (graphKey) {
        sortedData = filterOnlineOfflineData(sortedData, graphKey)
      }
      sortedData.sort((a, b) =>
        timeExtractor.difference(timeExtractor.getObservationTime(a), timeExtractor.getObservationTime(b)),
      )
      return {
        ...layer,
        data: sortedData,
      }
    })

  return [filteredLayers, loading]
}

export function getActionLayers(actions, timeExtractor, filteredLayers, multi = false, intl) {
  let columns = ["Action", "Priority", "Comment", "Action_Type"]
  if (multi) {
    columns = [...columns, "Name", "Reactor"]
  }
  if (arrayWithContents(actions)) {
    actions.sort((a, b) =>
      timeExtractor.difference(timeExtractor.getObservationTime(a), timeExtractor.getObservationTime(b)),
    )

    filteredLayers = filteredLayers.concat(
      ["Actor", "Operator"].map((param) => {
        return {
          tag: param,
          data: actions.map((l) => {
            const metric = l[param.toLowerCase()] || {}

            return {
              day: l.day,
              timestamp: l.timestamp,
              value: getLocaleFormattedUserString(metric, intl),
            }
          }),
        }
      }),
    )

    filteredLayers = filteredLayers.concat(
      columns.map((param) => {
        return {
          tag: param,
          data: actions.map((l) => {
            return {
              day: l.day,
              timestamp: l.timestamp,
              value: l[param.toLowerCase()],
            }
          }),
        }
      }),
    )
  }

  return filteredLayers
}

export function getColumnHeaders(
  filteredLayers,
  observationTypes = {},
  aligned_on,
  multiRun = false,
  rnaseq = false,
  intl,
) {
  const header_rename_map = {
    Timestamp: intl.formatMessage({ id: "graphing.timelabel.timestamp" }),
    Day: intl.formatMessage({ id: "graphing.timelabel.day" }),
    Action: intl.formatMessage({ id: "general.action" }),
    Comment: intl.formatMessage({ id: "general.comment" }),
    Priority: intl.formatMessage({ id: "general.priority" }),
    Actor: intl.formatMessage({ id: "general.actor" }),
    Operator: intl.formatMessage({ id: "general.operator" }),
    Name: intl.formatMessage({ id: "graphing.run.identifier" }),
    Reactor: intl.formatMessage({ id: "general.reactor" }),
  }

  const headers = multiRun
    ? [header_rename_map[aligned_on]]
    : [header_rename_map["Timestamp"], header_rename_map["Day"]]

  return [
    [
      [
        ...headers,
        ...filteredLayers.map((l) => {
          const tagInfo = observationTypes[l.tag] || {
            tag_name: l.tag,
            units: "unknown",
          }
          if (
            ![
              "Action",
              "Priority",
              "Comment",
              "Actor",
              "Operator",
              "Action_Type",
              "Name",
              "Reactor",
            ].includes(l.tag)
          ) {
            const suffix = multiRun ? ` [${l.name}]` : ""
            return `${tagInfo.label || tagInfo.tag} [${l.units || tagInfo.units || "unknown"}]${suffix}`
          } else {
            return header_rename_map[l.tag] || l.tag
          }
        }),
      ],
    ],
    [],
  ]
}

export function getRowData(
  filteredLayers,
  rows,
  additionalKeys, // LL concept
  timeExtractor,
  minTime,
  maxTime,
  multi,
  tzString, // LL concept
) {
  // Step through, finding groups of records with matched timestamps/days
  const indices = []
  for (let i = 0; i < filteredLayers.length; ++i) {
    indices[i] = 0
  }

  ALIGN_LOOP: while (true) {
    let alignTime = null,
      alignTimeRecord = null
    for (let i = 0; i < indices.length; ++i) {
      if (indices[i] < filteredLayers[i].data.length) {
        const nextTime = timeExtractor.getObservationTime(filteredLayers[i].data[indices[i]])
        if (alignTime === null || timeExtractor.difference(nextTime, alignTime) < 0) {
          alignTime = nextTime
          alignTimeRecord = filteredLayers[i].data[indices[i]]
        }
      }
    }
    if (alignTime === null) {
      // All data series exhausted.
      break ALIGN_LOOP
    }
    if (
      timeExtractor.difference(alignTime, minTime) >= 0 &&
      timeExtractor.difference(alignTime, maxTime) <= 0
    ) {
      const row = multi
        ? timeExtractor.name === "Day"
          ? [alignTimeRecord.day]
          : [convertTZ(alignTimeRecord.timestamp, tzString)]
        : [convertTZ(alignTimeRecord.timestamp, tzString), alignTimeRecord.day]
      for (let i = 0; i < indices.length; ++i) {
        if (
          indices[i] < filteredLayers[i].data.length &&
          timeExtractor.difference(
            timeExtractor.getObservationTime(filteredLayers[i].data[indices[i]]),
            alignTime,
          ) === 0
        ) {
          const obs = filteredLayers[i].data[indices[i]++] // Skips
          row.push(obs.note || obs.value)
        } else {
          row.push(null)
        }
      }
      rows.push(row)
    } else {
      // Skip to next
      for (let i = 0; i < indices.length; ++i) {
        if (
          indices[i] < filteredLayers[i].data.length &&
          timeExtractor.difference(
            timeExtractor.getObservationTime(filteredLayers[i].data[indices[i]]),
            alignTime,
          ) === 0
        ) {
          indices[i]++
        }
      }
    }
  }

  return rows
}

export function exportObservationsObject(
  timeExtractor,
  minTime,
  maxTime,
  run,
  layers,
  intl,
  graphKey,
  tzString,
) {
  const dataLayers = getDataLayers(layers, run.observationAccessor, run.name)
  const [filteredLayers, loading] = getFilteredDataLayers(dataLayers, timeExtractor, graphKey)
  const [rows, _] = getColumnHeaders(
    filteredLayers,
    run.observationTypesFlat,
    timeExtractor.name,
    false,
    false,
    intl,
  )

  return getRowData(filteredLayers, rows, _, timeExtractor, minTime, maxTime, false, tzString)
}

export function exportMultiRunObservationsObject(
  timeExtractor,
  minTime,
  maxTime,
  runs,
  observationTypes = {},
  layers,
  intl,
  graphKey,
  agg_reactors = false,
  tzString,
  siteWide = false,
) {
  let data = []

  if (!siteWide) {
    for (const layer of layers) {
      const run = runs.find((r) => r[id_accessor] === layer[id_accessor])
      if (run) {
        data.push(getDataLayers([layer], run.observationAccessor, run.name, run.reactor))
      }
    }
  } else {
    for (const run of runs) {
      data.push(getDataLayers(layers, run.observationAccessor, run.name, run.reactor))
    }
  }

  const dataLayers = [].concat.apply([], data)
  const [filteredLayers, loading] = getFilteredDataLayers(dataLayers, timeExtractor, graphKey)
  const [rows, _] = getColumnHeaders(filteredLayers, observationTypes, timeExtractor.name, true, false, intl)

  return getRowData(filteredLayers, rows, _, timeExtractor, minTime, maxTime, true, tzString)
}
